import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { FaGithub } from 'react-icons/fa';
import { FiExternalLink, FiX } from 'react-icons/fi';

// ── Tag colour helper ────────────────────────────────────────────────────────
const getTagStyle = (tag) => {
    const t = tag.toLowerCase();
    if (['next.js', 'react', 'laravel', 'node.js', 'vue'].some(k => t.includes(k)))
        return { backgroundColor: '#FFE600', color: '#111', border: '2px solid #111', boxShadow: '2px 2px 0 #111' };
    if (['mysql', 'postgresql', 'mongodb', 'prisma', 'supabase'].some(k => t.includes(k)))
        return { backgroundColor: '#4444FF', color: 'white', border: '2px solid #111', boxShadow: '2px 2px 0 #111' };
    if (['tailwind', 'bootstrap', 'css', 'scss', 'chakra'].some(k => t.includes(k)))
        return { backgroundColor: '#AAFF00', color: '#111', border: '2px solid #111', boxShadow: '2px 2px 0 #111' };
    return { backgroundColor: '#FF3366', color: 'white', border: '2px solid #111', boxShadow: '2px 2px 0 #111' };
};

// ── Parse "Problem: … Solution: … Impact: …" descriptions ───────────────────
const parseDescription = (desc = '') => {
    const result = { problem: '', solution: '', impact: '' };
    // Split on the keyword boundaries so multi-sentence segments survive
    const parts = desc.split(/(?=\bProblem:|Solution:|Impact:)/i);
    parts.forEach(part => {
        if (/^problem:/i.test(part))  result.problem  = part.replace(/^problem:/i,  '').trim().replace(/\.$/, '');
        if (/^solution:/i.test(part)) result.solution = part.replace(/^solution:/i, '').trim().replace(/\.$/, '');
        if (/^impact:/i.test(part))   result.impact   = part.replace(/^impact:/i,   '').trim().replace(/\.$/, '');
    });
    return result;
};

// ─────────────────────────────────────────────────────────────────────────────

const ProjectDetailModal = ({ project, onClose }) => {
    const backdropRef  = useRef(null);
    const modalRef     = useRef(null);
    const bodyRef      = useRef(null);   // scrollable body div
    const imageRef     = useRef(null);
    const closeBtnRef  = useRef(null);

    useEffect(() => {
        if (!project) return;

        // ── 1. Stop Lenis so it doesn't hijack wheel events behind modal ──
        //    Lenis listens for a custom event on the window.
        window.dispatchEvent(new CustomEvent('lenis:stop'));

        // ── 2. Fallback: also lock body overflow ──────────────────────────
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        // ── 3. GSAP open animation ────────────────────────────────────────
        const ctx = gsap.context(() => {
            gsap.set(backdropRef.current,  { opacity: 0 });
            gsap.set(modalRef.current,     { scale: 0.88, opacity: 0, y: 32, rotation: -1.5 });
            gsap.set('.modal-tag',         { opacity: 0, y: 16 });
            gsap.set(imageRef.current,     { clipPath: 'inset(0 100% 0 0)' });

            const tl = gsap.timeline();
            tl.to(backdropRef.current, { opacity: 1, duration: 0.25, ease: 'power2.out' })
              .to(modalRef.current, {
                  scale: 1, opacity: 1, y: 0, rotation: 0,
                  ease: 'elastic.out(1, 0.65)', duration: 0.65,
              }, '-=0.1')
              .to(imageRef.current, {
                  clipPath: 'inset(0 0% 0 0)', duration: 0.55, ease: 'power3.inOut',
              }, '-=0.3')
              .to('.modal-tag', {
                  opacity: 1, y: 0, stagger: 0.04, duration: 0.35, ease: 'power3.out',
              }, '-=0.15');

            // Close button spin on hover
            if (closeBtnRef.current) {
                closeBtnRef.current.addEventListener('mouseenter', () =>
                    gsap.to(closeBtnRef.current, { rotation: 90, scale: 1.15, duration: 0.22, ease: 'power2.out' })
                );
                closeBtnRef.current.addEventListener('mouseleave', () =>
                    gsap.to(closeBtnRef.current, { rotation: 0, scale: 1, duration: 0.28, ease: 'elastic.out(1,0.5)' })
                );
            }
        });

        return () => {
            ctx.revert();
            document.body.style.overflow = prevOverflow;
            // ── Resume Lenis when modal unmounts ──────────────────────────
            window.dispatchEvent(new CustomEvent('lenis:start'));
        };
    }, [project]);

    // ── Wheel isolation: prevent scroll from leaking to pinned section ───
    const stopWheelPropagation = (e) => {
        // Only block if the modal body itself isn't scrollable or at boundary
        if (!bodyRef.current) return;
        const el = bodyRef.current;
        const atTop    = el.scrollTop === 0 && e.deltaY < 0;
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight && e.deltaY > 0;
        if (atTop || atBottom) {
            e.preventDefault();
        }
        e.stopPropagation();
    };

    const handleClose = () => {
        gsap.to(modalRef.current, {
            scale: 0.9, opacity: 0, duration: 0.22, ease: 'power3.in',
            onComplete: onClose,
        });
        gsap.to(backdropRef.current, { opacity: 0, duration: 0.22 });
    };

    if (!project) return null;

    const parsed      = parseDescription(project.description || '');
    const hasStructured = parsed.problem || parsed.solution || parsed.impact;

    return (
        <div
            ref={backdropRef}
            onClick={handleClose}
            onWheel={(e) => e.stopPropagation()}   /* stop wheel on backdrop */
            style={{
                position: 'fixed', inset: 0,
                backgroundColor: 'rgba(17,17,17,0.85)',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px',
                overscrollBehavior: 'contain',
            }}
        >
            {/* ── Modal shell ─────────────────────────────────────────────── */}
            <div
                ref={modalRef}
                onClick={e => e.stopPropagation()}
                style={{
                    backgroundColor: '#F2EFE9',
                    border: '3px solid #111',
                    boxShadow: '10px 10px 0 #111',
                    borderRadius: 16,
                    width: '100%',
                    maxWidth: 'min(900px, 96vw)',
                    maxHeight: '90vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* ── Header bar ──────────────────────────────────────────── */}
                <div style={{
                    backgroundColor: '#111',
                    color: '#FFE600',
                    fontFamily: "'Space Mono', monospace",
                    fontWeight: 700,
                    padding: '12px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                }}>
                    <span style={{ fontSize: '0.75rem', letterSpacing: 2, textTransform: 'uppercase' }}>
                        ← PROJECTS
                    </span>
                    <button
                        ref={closeBtnRef}
                        onClick={handleClose}
                        aria-label="Close modal"
                        style={{
                            background: 'none', border: 'none',
                            color: '#FFE600', cursor: 'pointer',
                            padding: 4, display: 'flex', alignItems: 'center',
                        }}
                    >
                        <FiX size={22} />
                    </button>
                </div>

                {/* ── Scrollable body ──────────────────────────────────────── */}
                <div
                    ref={bodyRef}
                    onWheel={stopWheelPropagation}
                    style={{
                        overflowY: 'auto',
                        flex: 1,
                        padding: 'clamp(20px, 3vw, 32px)',
                        overscrollBehavior: 'contain',
                        /* custom scrollbar */
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#FFE600 transparent',
                    }}
                >
                    {/* ── Top: image + title side-by-side on md+ ──────────── */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
                        gap: 'clamp(16px, 3vw, 28px)',
                        marginBottom: 'clamp(20px, 3vw, 28px)',
                    }}>
                        {/* Left: image */}
                        <div>
                            <div
                                ref={imageRef}
                                style={{
                                    border: '3px solid #111',
                                    boxShadow: '5px 5px 0 #FFE600',
                                    borderRadius: 10,
                                    overflow: 'hidden',
                                    clipPath: 'inset(0 100% 0 0)',
                                }}
                            >
                                <img
                                    src={project.imageUrl}
                                    alt={project.title}
                                    style={{
                                        width: '100%',
                                        aspectRatio: '1181 / 1097',
                                        objectFit: 'cover',
                                        objectPosition: 'top center',
                                        display: 'block',
                                    }}
                                />
                            </div>
                        </div>

                        {/* Right: number + title + tags */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            {/* Number + title row */}
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                <span style={{
                                    fontFamily: "'Space Mono', monospace",
                                    fontSize: 'clamp(24px, 4vw, 36px)',
                                    fontWeight: 700,
                                    color: 'transparent',
                                    WebkitTextStroke: '2px #FFE600',
                                    lineHeight: 1,
                                    flexShrink: 0,
                                    userSelect: 'none',
                                    paddingTop: 2,
                                }}>
                                    {String(project.id || '01').padStart(2, '0')}
                                </span>
                                <h2 style={{
                                    fontFamily: "'Syne', sans-serif",
                                    fontWeight: 800,
                                    /* ← FIXED: was clamp(20px,4vw,var(--text-h2)) which could hit 40px+ */
                                    fontSize: 'clamp(18px, 2.2vw, 26px)',
                                    letterSpacing: '-0.025em',
                                    color: '#111',
                                    lineHeight: 1.2,
                                    margin: 0,
                                    wordBreak: 'break-word',
                                    hyphens: 'auto',
                                }}>
                                    {project.title}
                                </h2>
                            </div>

                            {/* Tech tags */}
                            <div>
                                <p style={{
                                    fontFamily: "'Space Mono', monospace",
                                    fontSize: 9,
                                    fontWeight: 700,
                                    color: '#111',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.18em',
                                    marginBottom: 8,
                                }}>
                                    TECH STACK
                                </p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                    {(project.tags || []).map(tag => (
                                        <span
                                            key={tag}
                                            className="modal-tag"
                                            style={{
                                                fontFamily: "'Space Mono', monospace",
                                                fontSize: 9,
                                                fontWeight: 700,
                                                letterSpacing: '0.08em',
                                                textTransform: 'uppercase',
                                                padding: '4px 10px',
                                                borderRadius: 4,
                                                ...getTagStyle(tag),
                                            }}
                                        >
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Company badge if present */}
                            {project.company && (
                                <p style={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: 12,
                                    color: '#888',
                                    margin: 0,
                                    fontStyle: 'italic',
                                }}>
                                    @ {project.company}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* ── Divider ──────────────────────────────────────────── */}
                    <div style={{
                        borderTop: '2px solid rgba(0,0,0,0.12)',
                        marginBottom: 'clamp(16px, 2vw, 24px)',
                    }} />

                    {/* ── Description ─────────────────────────────────────── */}
                    {hasStructured ? (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
                            gap: 'clamp(12px, 2vw, 20px)',
                            marginBottom: 'clamp(20px, 3vw, 28px)',
                        }}>
                            {[
                                { label: 'Problem',  value: parsed.problem,  accent: '#FF3366' },
                                { label: 'Solution', value: parsed.solution, accent: '#4444FF' },
                                { label: 'Impact',   value: parsed.impact,   accent: '#AAFF00' },
                            ].filter(r => r.value).map(row => (
                                <div key={row.label} style={{
                                    background: '#fff',
                                    border: '2px solid #111',
                                    borderRadius: 10,
                                    padding: 'clamp(12px, 2vw, 18px)',
                                    boxShadow: '3px 3px 0 #111',
                                }}>
                                    <span style={{
                                        fontFamily: "'Space Mono', monospace",
                                        fontSize: 9,
                                        fontWeight: 700,
                                        color: row.accent,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.18em',
                                        display: 'block',
                                        marginBottom: 6,
                                    }}>
                                        {row.label}:
                                    </span>
                                    <p style={{
                                        fontFamily: "'DM Sans', sans-serif",
                                        fontSize: 'clamp(12px, 1vw, 14px)',
                                        lineHeight: 1.65,
                                        color: '#444',
                                        margin: 0,
                                        maxWidth: '100%',
                                    }}>
                                        {row.value}.
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 'clamp(13px, 1vw, 15px)',
                            lineHeight: 1.7,
                            color: '#555',
                            marginBottom: 24,
                            maxWidth: '100%',
                        }}>
                            {project.description}
                        </p>
                    )}

                    {/* ── CTA Buttons ──────────────────────────────────────── */}
                    {(project.livePreviewUrl || project.githubUrl) && (
                        <div style={{
                            display: 'flex',
                            gap: 12,
                            flexWrap: 'wrap',
                        }}>
                            {project.livePreviewUrl && (
                                <a
                                    href={project.livePreviewUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 8,
                                        padding: '11px 22px',
                                        backgroundColor: '#FFE600', color: '#111',
                                        border: '2.5px solid #111', boxShadow: '4px 4px 0 #111',
                                        borderRadius: 8,
                                        fontWeight: 800, fontSize: '0.82rem',
                                        textDecoration: 'none',
                                        fontFamily: "'Syne', sans-serif",
                                        letterSpacing: '0.03em',
                                    }}
                                >
                                    <FiExternalLink size={15} /> Live Demo
                                </a>
                            )}
                            {project.githubUrl && (
                                <a
                                    href={project.githubUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 8,
                                        padding: '11px 22px',
                                        backgroundColor: '#111', color: 'white',
                                        border: '2.5px solid #111', boxShadow: '4px 4px 0 #111',
                                        borderRadius: 8,
                                        fontWeight: 800, fontSize: '0.82rem',
                                        textDecoration: 'none',
                                        fontFamily: "'Syne', sans-serif",
                                        letterSpacing: '0.03em',
                                    }}
                                >
                                    <FaGithub size={15} /> GitHub
                                </a>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProjectDetailModal;